import React from 'react';
import styled, { css } from 'styled-components';

import { Button, type ButtonProps } from '../../atoms/buttons/button/Button';

export type BottomActionsProps = {
	bottomPadding?: 'default' | 'large';
	buttonPropsArray: Array<ButtonProps>;
	disabled?: boolean;
	testID?: string;
};

/**
 *
 * @param {Array<ButtonProps>} buttonPropsArray Array of ButtonProps to be mapped into the Button component(s)
 * @param {boolean} disabled to set it disabled
 * @param {'default' | 'large'} bottomPadding the bottom padding variant to apply
 * @param {string} testID the test ID to apply to the component
 * @returns Bottom Actions Component
 */
export const BottomActions = ({
	bottomPadding = 'default',
	buttonPropsArray,
	disabled,
	testID
}: BottomActionsProps) => {
	// Design only allows a maximum of 2 buttons
	const slicedButtonPropsArray = buttonPropsArray?.slice(0, 2);

	return (
		<S.BottomActionsContainer $bottomPadding={bottomPadding} $disabled={disabled} data-testid={testID}>
			<S.ButtonContainer>
				{slicedButtonPropsArray?.map((buttonProps, index) => (
					<Button disabled={disabled} fullWidth key={index} {...buttonProps} />
				))}
			</S.ButtonContainer>
		</S.BottomActionsContainer>
	);
};

namespace S {
	export const BottomActionsContainer = styled.div<{
		$bottomPadding?: 'default' | 'large';
		$disabled?: boolean;
	}>`
		width: 100vw;
		position: fixed;
		bottom: 0;
		left: calc(-50vw + 50%);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 2;
		border-top-style: solid;
		border-top-width: ${({ theme }) => theme.border.width.small};
		border-color: ${({ theme }) => theme.colors.borders.main.divider};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.xsmall} ${({ theme }) => theme.spacings.small};

		${({ $disabled }) =>
			$disabled &&
			css`
				pointer-events: none;
			`};

		${({ $bottomPadding }) =>
			$bottomPadding === 'large' &&
			css`
				padding-bottom: ${({ theme }) => theme.spacings.large};
			`};
	`;

	export const ButtonContainer = styled.div`
		width: 100%;
		display: flex;
		column-gap: ${({ theme }) => theme.spacings.xxsmall};

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			width: 560px;
		}
		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			width: 1000px;
		}
	`;
}
